import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { WorkflowStepId } from '~/containers/viewer/explorer-sidebar/WorkflowStepper/workflow-steps';

/**
 * Search parameter naming the workflow step. The route names what is loaded;
 * which step is shown is a view of it. See `routes/paths.ts`.
 */
export const STEP_SEARCH_PARAM = 'step';

/**
 * Moves to a step, without the availability machinery — for callers that only
 * send the user somewhere: a finished task, or an open that has just put the
 * objects on screen.
 *
 * Asking for a step whose data has not arrived is allowed and useful: the
 * request stands in the URL and takes effect the moment it can be honoured.
 *
 * It lives here, in the routing layer, rather than beside the step table: that
 * table imports every step's sidebar content, and a caller that only writes a
 * search parameter has no business pulling the viewer in behind it.
 */
export function useSelectStep(): (id: WorkflowStepId) => void {
    const [, setSearchParams] = useSearchParams();
    return useCallback(
        (id: WorkflowStepId) => {
            setSearchParams(
                (previous) => {
                    const next = new URLSearchParams(previous);
                    next.set(STEP_SEARCH_PARAM, id);
                    return next;
                },
                // A step change is a navigation the user made and can undo, so
                // it belongs in history — unlike the objects the step is shown
                // on, which the open replaces.
                { replace: false }
            );
        },
        [setSearchParams]
    );
}
